import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import HowToRegOutlined from '@mui/icons-material/HowToRegOutlined';
import PaidOutlined from '@mui/icons-material/PaidOutlined';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import UndoOutlined from '@mui/icons-material/UndoOutlined';
import { Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { formatMoney, type MoneyLanguage } from '../../shared/format/money';
import { StatRow } from '../../shared/stat/StatRow';
import { StatTile } from '../../shared/stat/StatTile';
import { reportKindLabel } from '../reports/labels';
import { reportDrilldownHref } from '../reports/ReportDrilldowns';
import type { ReportKind } from '../reports/types';
import { countFormat } from './trendSeries';
import type { DashboardCurrencyTotals, DashboardFilters, DashboardSummary } from './types';

export function KpiGrid({
  filters,
  summary,
}: {
  readonly filters: DashboardFilters;
  readonly summary: DashboardSummary;
}) {
  const { i18n, t } = useTranslation();
  const language: MoneyLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const href = (kind: ReportKind, overrides: Readonly<Record<string, string>> = {}): string =>
    reportDrilldownHref(kind, { ...filters, ...overrides });
  const link = (kind: ReportKind, overrides: Readonly<Record<string, string>> = {}): KpiLink => ({
    href: href(kind, overrides),
    label: reportKindLabel(kind, t),
  });
  const countFormatInstance = countFormat(language);
  const count = (value: number): string => countFormatInstance.format(value);

  return (
    <Stack spacing={2}>
      <StatRow ariaLabel={t('dashboard.summary')}>
        <StatTile
          icon={PaidOutlined}
          label={t('dashboard.kpis.totalActualPlay')}
          tint="blue"
          to={href('transactions')}
          value={
            <MoneyValue currencies={summary.currencies} field="playAmount" language={language} />
          }
        />
        {/*
          A tile that is itself a link cannot contain more links. Net commission and commission
          reversals each need two drill-down links (agent commissions, affiliate commissions), so
          these two tiles render as plain (non-linked) tiles with the drill-downs in the caption
          slot instead of wrapping the whole tile in a link to one report.
        */}
        <StatTile
          caption={
            <DrilldownLinks
              label={t('dashboard.kpis.netCommission')}
              links={[link('agent-commissions'), link('affiliate-commissions')]}
            />
          }
          icon={AccountBalanceWalletOutlined}
          label={t('dashboard.kpis.netCommission')}
          tint="teal"
          value={
            <MoneyValue currencies={summary.currencies} field="netCommission" language={language} />
          }
        />
        <StatTile
          caption={
            <DrilldownLinks
              label={t('dashboard.kpis.commissionReversals')}
              links={[
                link('agent-commissions', { entryType: 'REVERSAL' }),
                link('affiliate-commissions', { entryType: 'REVERSAL' }),
              ]}
            />
          }
          icon={UndoOutlined}
          label={t('dashboard.kpis.commissionReversals')}
          tint="orange"
          value={
            <MoneyValue
              currencies={summary.currencies}
              field="commissionReversals"
              language={language}
            />
          }
        />
        <StatTile
          icon={SwapHorizOutlined}
          label={t('dashboard.kpis.transactions')}
          tint="violet"
          to={href('transactions')}
          value={
            <Typography component="p" variant="h4">
              {count(summary.transactionCount)}
            </Typography>
          }
        />
      </StatRow>

      <StatRow ariaLabel={t('dashboard.operationalQueues')} columns={5}>
        <StatTile
          icon={TaskAltOutlined}
          label={t('dashboard.kpis.calculated')}
          tint="teal"
          to={href('transactions', { status: 'CALCULATED' })}
          value={
            <Typography component="p" variant="h5">
              {count(summary.calculatedTransactionCount)}
            </Typography>
          }
        />
        <StatTile
          icon={UndoOutlined}
          label={t('dashboard.kpis.reversed')}
          tint="amber"
          to={href('transactions', { status: 'REJECTED' })}
          value={
            <Typography component="p" variant="h5">
              {count(summary.rejectedTransactionCount)}
            </Typography>
          }
        />
        <StatTile
          icon={ReportProblemOutlined}
          label={t('dashboard.kpis.failed')}
          tint="orange"
          to={href('transactions', { status: 'FAILED' })}
          value={
            <Typography component="p" variant="h5">
              {count(summary.failedTransactionCount)}
            </Typography>
          }
        />
        <StatTile
          icon={GroupsOutlined}
          label={t('dashboard.kpis.activeUsers')}
          tint="blue"
          to={href('user-affiliate-by-game')}
          value={
            <Typography component="p" variant="h5">
              {count(summary.activeUserCount)}
            </Typography>
          }
        />
        <StatTile
          icon={HowToRegOutlined}
          label={t('dashboard.kpis.registrations')}
          tint="violet"
          to={href('registrations', { status: '' })}
          value={
            <Typography component="p" variant="h5">
              {count(summary.registrationCount)}
            </Typography>
          }
        />
      </StatRow>
    </Stack>
  );
}

function DrilldownLinks({
  label,
  links,
}: {
  readonly label: string;
  readonly links: readonly KpiLink[];
}) {
  return (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }} useFlexGap>
      {links.map((drilldown) => (
        <Button
          aria-label={`${label} · ${drilldown.label}`}
          component={Link}
          key={drilldown.href}
          size="small"
          to={drilldown.href}
        >
          {drilldown.label}
        </Button>
      ))}
    </Stack>
  );
}

function MoneyValue({
  currencies,
  field,
  language,
}: {
  readonly currencies: readonly DashboardCurrencyTotals[];
  readonly field: 'playAmount' | 'netCommission' | 'commissionReversals';
  readonly language: MoneyLanguage;
}) {
  return (
    <Stack spacing={0.25}>
      {currencies.map((currency) => (
        <Typography component="p" key={currency.currencyCode} variant="h5">
          {formatMoney(currency[field], currency.currencyCode, language)}
        </Typography>
      ))}
    </Stack>
  );
}

interface KpiLink {
  readonly href: string;
  readonly label: string;
}
